import axios from 'axios';
import store from '@/store';
import Banner from '@/objects/Banner';
import { isLocalhost } from '@/shared/utility';

export default class NetworkConnectionManager {
  /**
   * Minimum time interval in milliseconds to check the server state
   */
  static CHECK_SERVER_STATE_INTERVAL = 10 * 1000;

  /**
   * Minimum time interval in milliseconds to check the bandwidth
   * Use infinity now to check it only once, later can be changed to smaller steps
   */
  static CHECK_BANDWIDTH_INTERVAL = 60 * 1000;

  /**
   * Minimum size in bytes (1 MB) of the bandwidth test file, below which will be considered invalid file
   */
  static MIN_SIZE_OF_BANDWIDTH_FILE = 1000000;

  /**
   * Minimum bandwidth for the web to work properly. Number below this is considered a poor bandwidth
   */
  static MIN_BANDWIDTH = 400;

  /**
   * Last unix time in milliseconds to check whether the server is down for maintenance or not
   */
  static lastTimeCheckServer = 0;

  /**
   * Last unix time in milliseconds to check whether the bandwidth is poor or not
   */
  static lastTimeCheckBandwidth = 0;

  /**
   * Time in milliseconds before the request times out
   */
  static REQUEST_TIMEOUT = 30 * 1000;

  static async checkServerState() {
    // Skip the check for localhost
    if (isLocalhost()) {
      return;
    }

    try {
      const currentTime = new Date().getTime();

      if (
        currentTime > this.lastTimeCheckServer &&
        currentTime - this.lastTimeCheckServer <
          this.CHECK_SERVER_STATE_INTERVAL
      ) {
        // Already checked the server state within [${this.CHECK_SERVER_STATE_INTERVAL/1000} seconds]
        console.log(
          `Already checked the server state within [${
            this.CHECK_SERVER_STATE_INTERVAL / 1000
          } seconds]`
        );
        return;
      }

      this.lastTimeCheckServer = currentTime;

      const alertFileUrl = '/images/server-alert.json';
      const axiosConfig = {
        url: alertFileUrl,
        timeout: this.REQUEST_TIMEOUT,
      };
      const response = await axios.get(alertFileUrl, axiosConfig);
      this.logResult('checkServerState', response);

      this.onCheckServerStateResponse(response);
    } catch (error) {
      this.logError('checkServerState', error);
    }
  }

  static onCheckServerStateResponse(response) {
    const typeInStore = store.getters['banner/type'];

    switch (response.data.Type) {
      case Banner.TYPE_UNDER_MAINTENANCE:
        Banner.show(Banner.TYPE_UNDER_MAINTENANCE);
        break;
      default:
        // Nothing to be shown for the server state. Close the banner if current banner is showing the server state info
        if (typeInStore == Banner.TYPE_UNDER_MAINTENANCE) {
          Banner.reset();
        }
        break;
    }
  }

  static async checkBandwidth() {
    // Skip the check for localhost
    if (isLocalhost()) {
      return;
    }

    try {
      const currentTime = new Date().getTime();

      if (
        currentTime > this.lastTimeCheckBandwidth &&
        currentTime - this.lastTimeCheckBandwidth <
          this.CHECK_BANDWIDTH_INTERVAL
      ) {
        // Already checked the bandwidth within [${this.CHECK_BANDWIDTH_INTERVAL/1000} seconds]
        console.log(
          `Already checked the bandwidth within [${
            this.CHECK_BANDWIDTH_INTERVAL / 1000
          } seconds]`
        );
        return;
      }

      this.lastTimeCheckBandwidth = currentTime;

      const bandwidthFileUrl = '/images/bandwidth-detection.png';
      const axiosConfig = {
        url: bandwidthFileUrl,
        timeout: this.REQUEST_TIMEOUT,
        responseType: 'arraybuffer',
      };
      const response = await axios.get(bandwidthFileUrl, axiosConfig);
      this.logResult('checkBandwidth', response);

      const endTime = new Date().getTime();
      const timeTaken = endTime - currentTime;

      this.onCheckBandwidthResponse(response, timeTaken);
    } catch (error) {
      this.logError('checkBandwidth', error);
      this.onCheckBandwidthError(error);
    }
  }

  static onCheckBandwidthResponse(response, timeTakenMs) {
    const contentType = response.headers['content-type'];
    if (contentType !== 'image/png') {
      console.log(
        `Invalid content. Received content type [${contentType}] instead of [image/png]`
      );
      return;
    }

    const timeTakenMsInt = Number.parseInt(timeTakenMs);
    const buffer = Buffer.from(response.data);
    const fileSizeBytes = buffer.length;

    console.log(
      `Got bandwidth test file with size [${fileSizeBytes} bytes] in [${timeTakenMs} milliseconds]`
    );

    const typeInStore = store.getters['banner/type'];

    if (
      fileSizeBytes < this.MIN_SIZE_OF_BANDWIDTH_FILE ||
      Number.isNaN(timeTakenMsInt) ||
      timeTakenMsInt <= 0
    ) {
      // Invalid file or time, reset banner if current banner is the type of poor bandwidth
      if (typeInStore == Banner.TYPE_POOR_BANDWIDTH) {
        Banner.reset();
      }

      console.log('Invalid test file or time taken for bandwidth detection');
      return;
    }

    const timeTakenSeconds = timeTakenMs / 1000.0;
    const currentBandwidth = Number.parseInt(
      fileSizeBytes / 1000.0 / timeTakenSeconds
    );
    console.log(`Current bandwidth: [${currentBandwidth} kbps]`);

    if (currentBandwidth < this.MIN_BANDWIDTH) {
      // Poor bandwith
      console.log(
        `Poor bandwidth. Going to show the banner of the type [${Banner.TYPE_POOR_BANDWIDTH}]`
      );
      Banner.show(Banner.TYPE_POOR_BANDWIDTH);
    } else if (typeInStore == Banner.TYPE_POOR_BANDWIDTH) {
      // Good bandwidth, close the banner of type poor bandwidth
      console.log(
        `Good bandwidth. Going to close the banner of the type [${Banner.TYPE_POOR_BANDWIDTH}]`
      );
      Banner.reset();
    } else {
      // Good bandwidth, but current banner is not the type of poor bandwidth. Nothing needs to be done
      console.log(
        `Good bandwidth. Banner of the type [${Banner.TYPE_POOR_BANDWIDTH}] is not visible. Nothing needs to be done.`
      );
    }
  }

  static onCheckBandwidthError(error) {
    if (error.code === 'ECONNABORTED') {
      // Request timed out, which is considered poor bandwidth
      console.log(
        `checkBandwidth timed out. Going to show the banner of the type [${Banner.TYPE_POOR_BANDWIDTH}]`
      );
      Banner.show(Banner.TYPE_POOR_BANDWIDTH);
    }
  }

  static logResult(request, result) {
    console.groupCollapsed(
      `%cThe call [${request}] on [NetworkConnectionManager] was successful`,
      'background-color: #81ad61; color: white; border-radius: 25px; padding: 0 5px'
    );
    console.log('Request result: ', result);
    console.groupEnd();
  }

  static logError(request, error) {
    console.groupCollapsed(
      `%cThe call [${request}] on [NetworkConnectionManager] was not successful`,
      'background-color: #f3584d; color: white; border-radius: 25px; padding: 0 5px'
    );
    console.log('Request error: ', error);
    console.groupEnd();
  }
}
